import { parseGIF, decompressFrames } from 'gifuct-js';
import { AUDIO_MIMETYPES, IMAGE_MIMETYPES, VIDEO_MIMETYPES } from '@constants';
import { IMediaMetadata } from '@interface/media';

export const isPowersOfTwo = (n: number) => {
  return !(2 ** Math.ceil(Math.log2(n)) - n);
};

export const getValidSizes = (
  sizes: number[],
  actual: number,
  ensureActualMin: boolean,
  ensureActualMax: boolean
): number[] => {
  const valid = sizes.filter((size) => size < actual);
  if (ensureActualMax && valid[valid.length - 1] < actual) {
    valid.push(actual);
  }

  if (ensureActualMin && !valid.length) {
    valid.push(actual);
  }

  return valid;
};

export const blobToFile = (blob: Blob, fileName: string): File => {
  return new File([blob], fileName, { lastModified: Date.now(), type: blob.type });
};

export const getExtension = (str: string) => {
  return str.split('.').pop();
};

export const getImageMetadata = (blob: Blob): Promise<IMediaMetadata> => {
  return new Promise<IMediaMetadata>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
    };
    img.onerror = (err) => {
      reject(err);
    };
    img.src = URL.createObjectURL(blob);
  });
};

export const getGIFInfo = async (blob: Blob) => {
  const buff = await blob.arrayBuffer();
  const gif = await parseGIF(buff);
  const frames = await decompressFrames(gif, true);
  const delays = frames.map((frame) => frame.delay);
  const duration = delays.reduce((v1, v2) => v1 + v2) / 1000;
  return {
    frameCount: frames.length,
    duration,
    width: gif.lsd.width,
    height: gif.lsd.height,
  };
};

export const getAudioMetadata = (blob: Blob): Promise<IMediaMetadata> => {
  return new Promise<IMediaMetadata>((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.addEventListener(
      'loadedmetadata',
      () => {
        resolve({
          duration: audio.duration,
        });
      },
      false
    );
    audio.addEventListener('error', (err) => {
      reject(err);
    });

    audio.src = URL.createObjectURL(blob);
  });
};

export const getVideoMetadata = (blob: Blob): Promise<IMediaMetadata> => {
  return new Promise<IMediaMetadata>((resolve, reject) => {
    const video = document.createElement('video');
    video.addEventListener(
      'loadedmetadata',
      () => {
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration,
        });
      },
      false
    );
    video.addEventListener('error', (err) => {
      reject(err);
    });

    video.src = URL.createObjectURL(blob);
  });
};

export const getMediaMetadata = async (file: File) => {
  let meta: IMediaMetadata;
  if (VIDEO_MIMETYPES.includes(file.type)) {
    meta = await getVideoMetadata(file);
  } else if (IMAGE_MIMETYPES.includes(file.type)) {
    meta = await getImageMetadata(file);
  } else if (AUDIO_MIMETYPES.includes(file.type)) {
    meta = await getAudioMetadata(file);
  } else {
    meta = {};
  }
  return meta;
};
